import React, { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { ExpandMore } from "@mui/icons-material";
import { MissingManifestResponse } from "../../app/models/responseModels/missingManifestResponse";
import {
  MISSING_MANIFEST_COLUMN_NAME,
  MISSING_MANIFEST_FILTER_COLUMN_OPTIONS,
  MISSING_MANIFEST_LABEL_NAME,
} from "./MissingmanifestConstants";
import { DatePicker } from "@mui/x-date-pickers";
import { addDays } from "date-fns";
import AutoCompleteFilter from "../../components/common/autoCompleteFilter/AutoCompleteFilter";
import { CustomTooltip } from "../../components/common/tooltip/CustomToolTip";

const COLUMN_WITH_DATE = ["arrivalDate"];

type MissingManifestDataFilterProps = {
  missingManifestData: MissingManifestResponse[];
  setFilteredData: React.Dispatch<
    React.SetStateAction<MissingManifestResponse[]>
  >;
  filteredData: MissingManifestResponse[];
};

type FilterOptions = {
  columnName: string;
  inputRank: number | null;
  displayColumnName: string;
};

const MissingManifestDataFilter = ({
  missingManifestData,
  setFilteredData,
  filteredData,
}: MissingManifestDataFilterProps) => {
  // ***** States *****
  const [open, setOpen] = useState(false);

  const [inputList, setInputList] = useState<FilterOptions[]>([
    {
      columnName: "",
      inputRank: null,
      displayColumnName: "",
    },
  ]);

  const [trackingNumberFilter, setTrackingNumberFilter] = useState<string>("");

  const [locationNameOptions, setLocationNameOptions] = useState<string[]>([]); //Dropdown Options
  const [locationNameSelectedOptions, setLocationNameSelectedOptions] =
    useState<string[]>([]); //Dropdown selected value

  const [arrivalFromDate, setArrivalFromDate] = useState<Date | null>(null);
  const [arrivalToDate, setArrivalToDate] = useState<Date | null>(null);

  const toggle = () => {
    setOpen(!open);
  };

  // ***** Callbacks *****
  const getFilterOptions = useCallback(
    (columnName: string) => {
      if (columnName === MISSING_MANIFEST_COLUMN_NAME.LOCATION_NAME) {
        return locationNameOptions;
      }
      return [];
    },
    [locationNameOptions]
  );

  const getSelectedOptions = useCallback(
    (columnName: string) => {
      if (columnName === MISSING_MANIFEST_COLUMN_NAME.LOCATION_NAME) {
        return locationNameSelectedOptions;
      }
      return [];
    },
    [locationNameSelectedOptions]
  );

  const getSelectedFromDate = useCallback(
    (columnName: string) => {
      if (columnName === MISSING_MANIFEST_COLUMN_NAME.ARRIVAL_DATE) {
        return arrivalFromDate;
      }
      return null;
    },
    [arrivalFromDate]
  );

  const getSelectedToDate = useCallback(
    (columnName: string) => {
      if (columnName === MISSING_MANIFEST_COLUMN_NAME.ARRIVAL_DATE) {
        return arrivalToDate;
      }
      return null;
    },
    [arrivalToDate]
  );

  // ***** UseEffects *****
  useEffect(() => {
    const selectedColumnName = inputList[0].columnName;
    //LocationName dropdown options
    if (selectedColumnName === MISSING_MANIFEST_COLUMN_NAME.LOCATION_NAME) {
      setLocationNameOptions(
        missingManifestData
          .map((v) => v.locationName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    } else {
      setLocationNameOptions(
        filteredData
          .map((v) => v.locationName)
          .filter((value, index, array) => array.indexOf(value) === index)
      );
    }
  }, [filteredData, inputList, missingManifestData]);

  useEffect(() => {
    let newData = [...missingManifestData];
    //LocationName Filter
    if (locationNameSelectedOptions.length > 0) {
      newData = newData.filter((a) => {
        return locationNameSelectedOptions.some((s) => {
          return s === a.locationName;
        });
      });
    }

    //Tracking Filter
    if (trackingNumberFilter) {
      if (trackingNumberFilter.includes(",")) {
        const trackingNumbers = trackingNumberFilter.split(",");
        newData = newData.filter((a) => {
          return trackingNumbers.some((s) => {
            return s === a.trackingNumber;
          });
        });
      } else {
        newData = newData.filter((x) =>
          x.trackingNumber
            .toLowerCase()
            .includes(trackingNumberFilter.toLowerCase())
        );
      }
    }

    if (arrivalFromDate && arrivalToDate) {
      newData = newData.filter(
        (x) =>
          new Date(x.arrivalDate).toLocaleDateString() >=
            arrivalFromDate.toLocaleDateString() &&
          new Date(x.arrivalDate).toLocaleDateString() <=
            arrivalToDate.toLocaleDateString()
      );
    }
    setFilteredData(newData);
  }, [
    missingManifestData,
    setFilteredData,
    trackingNumberFilter,
    locationNameSelectedOptions,
    arrivalFromDate,
    arrivalToDate,
  ]);

  // ***** Handlers *****
  const handleTrackingNumberChange = (e: any) => {
    let trackingNumbers = e.target.value;
    if (
      trackingNumbers.includes(" ") ||
      trackingNumbers.includes("\t") ||
      trackingNumbers.includes(",")
    ) {
      setTrackingNumberFilter(
        trackingNumbers.split(" ").join(",").split("\t").join(",")
      );
    } else {
      setTrackingNumberFilter(trackingNumbers);
    }
  };

  const handleListAdd = (index: number) => {
    setInputList([
      ...inputList,
      {
        columnName: "",
        inputRank: null,
        displayColumnName: "",
      },
    ]);
  };

  const handleRemoveItem = (index: number): void => {
    const newList: Array<FilterOptions> = [...inputList];
    newList.splice(index, 1);
    setInputList(newList);

    const removedColumnName = inputList[index].columnName;
    if (removedColumnName === MISSING_MANIFEST_COLUMN_NAME.LOCATION_NAME) {
      return setLocationNameSelectedOptions([]);
    }
    if (removedColumnName === MISSING_MANIFEST_COLUMN_NAME.ARRIVAL_DATE) {
      setArrivalFromDate(null);
      setArrivalToDate(null);
      return;
    }
  };

  const handleColumnNameChange = (event: any, index: number) => {
    const { value } = event.target;
    const newInputList = [...inputList];
    const oldColumnName = newInputList[index].columnName;

    if (oldColumnName.length > 0) {
      if (oldColumnName === MISSING_MANIFEST_COLUMN_NAME.LOCATION_NAME) {
        setLocationNameSelectedOptions([]);
      }
      if (oldColumnName === MISSING_MANIFEST_COLUMN_NAME.ARRIVAL_DATE) {
        setArrivalFromDate(null);
        setArrivalToDate(null);
      }
    }
    if (value === MISSING_MANIFEST_COLUMN_NAME.LOCATION_NAME) {
      newInputList[index].displayColumnName =
        MISSING_MANIFEST_LABEL_NAME.LOCATION_NAME;
    }
    if (value === MISSING_MANIFEST_COLUMN_NAME.ARRIVAL_DATE) {
      newInputList[index].displayColumnName = "Arrival";
    }
    newInputList[index].columnName = value;
    newInputList[index].inputRank = index;
    setInputList(newInputList);
  };

  const handleReset = (e: any) => {
    setTrackingNumberFilter("");
    setLocationNameSelectedOptions([]);
    setArrivalFromDate(null);
    setArrivalToDate(null);
    setInputList([
      {
        columnName: "",
        inputRank: null,
        displayColumnName: "",
      },
    ]);
  };

  const handleSelectedOption = (
    event: Event,
    columnName: string,
    value: string[]
  ) => {
    if (columnName === MISSING_MANIFEST_COLUMN_NAME.LOCATION_NAME) {
      return setLocationNameSelectedOptions(value);
    }
  };

  const handleFromDateChange = (value: Date | null, columnName: string) => {
    if (columnName === MISSING_MANIFEST_COLUMN_NAME.ARRIVAL_DATE) {
      return setArrivalFromDate(value);
    }
  };

  const handleToDateChange = (value: Date | null, columnName: string) => {
    if (columnName === MISSING_MANIFEST_COLUMN_NAME.ARRIVAL_DATE) {
      return setArrivalToDate(value);
    }
  };

  return (
    <div>
      <Accordion elevation={5} expanded={open}>
        <AccordionSummary
          onClick={toggle}
          expandIcon={
            <>
              <IconButton onClick={toggle} sx={{ color: "white" }}>
                <ExpandMore sx={{ color: "white" }} />
              </IconButton>
            </>
          }
          aria-controls="panel1a-content"
          id="panel1a-header"
          sx={{
            minHeight: "36px",
            "& .MuiAccordionSummary-content": {
              margin: 0,
            },
            "& .MuiAccordionSummary-content.Mui-expanded": {
              margin: 0,
            },

            "&.Mui-expanded": {
              minHeight: "36px",
            },
            backgroundColor: "primary.main",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              color: "white",
            }}
          >
            <Typography>Search Missing Manifest</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ maxHeight: 440, overflowY: "scroll" }}>
          {/* sx={{ height: 220, overflowY: "scroll" }} */}
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            {inputList.map((input, index) => (
              <Grid container rowSpacing={2} columnSpacing={2} key={index}>
                <Grid item xs={12} sm={5} md={5} lg={5}>
                  <TextField
                    select
                    key={"columnName"}
                    margin="normal"
                    id={`columnName${index}`}
                    label="Column Name"
                    name={`columnName${index}`}
                    size="small"
                    fullWidth
                    value={inputList[index].columnName}
                    onChange={(event) => handleColumnNameChange(event, index)}
                  >
                    {MISSING_MANIFEST_FILTER_COLUMN_OPTIONS.map((item) => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={5} md={5} lg={5}>
                  {COLUMN_WITH_DATE.includes(inputList[index].columnName) ? (
                    <Grid container rowSpacing={2} columnSpacing={2}>
                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        {/* <MuiDateRangePicker /> */}
                        <DatePicker
                          minDate={addDays(new Date(), -90)}
                          maxDate={addDays(new Date(), 30)}
                          label={`${inputList[index].displayColumnName} From Date`}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              margin: "normal",
                            },
                            popper: {
                              placement: "auto",
                            },
                          }}
                          onChange={(newValue) =>
                            handleFromDateChange(
                              newValue,
                              inputList[index].columnName
                            )
                          }
                          value={getSelectedFromDate(
                            inputList[index].columnName
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={6} lg={6}>
                        <DatePicker
                          minDate={addDays(new Date(), -90)}
                          maxDate={addDays(new Date(), 30)}
                          label={`${inputList[index].displayColumnName} To Date`}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                              margin: "normal",
                            },
                            popper: {
                              placement: "auto",
                            },
                          }}
                          onChange={(newValue) =>
                            handleToDateChange(
                              newValue,
                              inputList[index].columnName
                            )
                          }
                          value={getSelectedToDate(inputList[index].columnName)}
                        />
                      </Grid>
                    </Grid>
                  ) : (
                    <AutoCompleteFilter
                      columnName={inputList[index].columnName}
                      displayColumnName={inputList[index].displayColumnName}
                      options={getFilterOptions(inputList[index].columnName)}
                      value={getSelectedOptions(inputList[index].columnName)}
                      onChange={(event: any, selectedValue: string[]) => {
                        handleSelectedOption(
                          event,
                          inputList[index].columnName,
                          selectedValue
                        );
                      }}
                    />
                  )}
                </Grid>
                <Grid
                  item
                  xs={12}
                  sm={2}
                  md={2}
                  lg={2}
                  sx={{ alignSelf: "center" }}
                >
                  <CustomTooltip arrow title="Add Filter">
                    <span>
                      <IconButton
                        onClick={() => handleListAdd(index + 1)}
                        disabled={inputList.length === 10}
                      >
                        <AddIcon sx={{ color: "primary.main" }} />
                      </IconButton>
                    </span>
                  </CustomTooltip>
                  <CustomTooltip arrow title="Remove Filter">
                    <span>
                      <IconButton
                        onClick={() => handleRemoveItem(index)}
                        disabled={inputList.length === 1}
                      >
                        <RemoveIcon sx={{ color: "warning.dark" }} />
                      </IconButton>
                    </span>
                  </CustomTooltip>
                </Grid>
              </Grid>
            ))}
            {/* Tracking Number Filter */}
            <Grid container rowSpacing={2} columnSpacing={2}>
              <Grid item xs={12} sm={12} md={12} lg={12}>
                <TextField
                  key={"trackingNumber"}
                  margin="normal"
                  id="trackingNumber"
                  label="Tracking Number"
                  name="trackingNumber"
                  size="small"
                  fullWidth
                  value={trackingNumberFilter}
                  onChange={handleTrackingNumberChange}
                />
              </Grid>
            </Grid>
          </Box>
          <Grid container spacing={2}>
            <Grid xs={12} item>
              <>
                <Box display={"flex"} justifyContent="end">
                  <Button
                    variant="contained"
                    sx={{ mt: 2 }}
                    onClick={handleReset}
                  >
                    Reset
                  </Button>
                </Box>
              </>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default MissingManifestDataFilter;
